// app/features/templates/components/TemplateCardComponent.tsx
"use client";

import { BookOpen, Briefcase, Code, FileText, Target } from 'lucide-react';
import Link from 'next/link';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { Template } from '../templates.interface';

interface TemplateCardProps {
  template: Template;
  backgroundColorVariant?: 'default' | 'surface' | 'surfaceLight' | 'beige' | 'beigeLight';
}

// Generate a consistent color scheme based on category slug
const getCategoryConfig = (categorySlug?: string | null, categoryName?: string | null) => {
  // Default fallback
  const defaultConfig = { 
    icon: FileText, 
    label: 'Template',
    color: 'text-slate-700',
    accentBgClass: 'bg-slate-500',
    iconContainerBgClass: 'bg-slate-100',
    iconTextColorClass: 'text-slate-600',
    iconBorderClass: 'border-slate-200'
  };

  if (!categorySlug) return defaultConfig;

  // Map specific categories we know about - Updated with vibrant colors to match AI models
  const categoryMap: Record<string, any> = {
    'prompt-techniques': {
      icon: null, // No icon for now since we're not using database icons
      color: 'text-yellow-700',
      accentBgClass: 'bg-yellow', // Using brand primary yellow
      iconContainerBgClass: 'bg-yellow-100',
      iconTextColorClass: 'text-yellow-600',
      iconBorderClass: 'border-yellow-200'
    },
    'agent': {
      icon: null, // No icon for now since we're not using database icons
      color: 'text-green-700',
      accentBgClass: 'bg-green', // Using brand green (supporting)
      iconContainerBgClass: 'bg-green-100',
      iconTextColorClass: 'text-green-600',
      iconBorderClass: 'border-green-200'
    },
    'agent-frameworks': {
      icon: Code,
      color: 'text-green-700',
      accentBgClass: 'bg-green', // Using brand green (supporting)
      iconContainerBgClass: 'bg-green-100',
      iconTextColorClass: 'text-green-600',
      iconBorderClass: 'border-green-200'
    },
    'agentic-systems': {
      icon: Briefcase,
      color: 'text-purple-700',
      accentBgClass: 'bg-purple', // Using brand purple (supporting)
      iconContainerBgClass: 'bg-purple-100',
      iconTextColorClass: 'text-purple-600',
      iconBorderClass: 'border-purple-200'
    },
    'knowledge-management': {
      icon: BookOpen,
      color: 'text-orange-700',
      accentBgClass: 'bg-orange', // Using brand orange (supporting)
      iconContainerBgClass: 'bg-orange-100',
      iconTextColorClass: 'text-orange-600',
      iconBorderClass: 'border-orange-200'
    },
    'productivity': {
      icon: null, // No icon for now since we're not using database icons
      color: 'text-teal-700',
      accentBgClass: 'bg-teal', // Using brand teal (secondary)
      iconContainerBgClass: 'bg-teal-100',
      iconTextColorClass: 'text-teal-600',
      iconBorderClass: 'border-teal-200'
    },
    'context': {
      icon: Target,
      color: 'text-indigo-700',
      accentBgClass: 'bg-indigo', // Keeping indigo as it's already vibrant
      iconContainerBgClass: 'bg-indigo-100',
      iconTextColorClass: 'text-indigo-600',
      iconBorderClass: 'border-indigo-200'
    }
  };

  // Return known category config or generate one dynamically
  if (categoryMap[categorySlug]) {
    return {
      ...categoryMap[categorySlug],
      label: categoryName || categoryMap[categorySlug].label || categorySlug
    };
  }

  // For unknown categories, generate a consistent color based on hash
  const colors = [
    {
      color: 'text-rose-700',
      accentBgClass: 'bg-rose-500',
      iconContainerBgClass: 'bg-rose-100',
      iconTextColorClass: 'text-rose-600',
      iconBorderClass: 'border-rose-200'
    },
    {
      color: 'text-orange-700',
      accentBgClass: 'bg-orange-500',
      iconContainerBgClass: 'bg-orange-100',
      iconTextColorClass: 'text-orange-600',
      iconBorderClass: 'border-orange-200'
    },
    {
      color: 'text-teal-700',
      accentBgClass: 'bg-teal-500',
      iconContainerBgClass: 'bg-teal-100',
      iconTextColorClass: 'text-teal-600',
      iconBorderClass: 'border-teal-200'
    },
    {
      color: 'text-purple-700',
      accentBgClass: 'bg-purple-500',
      iconContainerBgClass: 'bg-purple-100',
      iconTextColorClass: 'text-purple-600',
      iconBorderClass: 'border-purple-200'
    }
  ];

  // Simple hash function to get consistent color for unknown categories
  const hash = categorySlug.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  const colorIndex = Math.abs(hash) % colors.length;

  return {
    icon: FileText,
    label: categoryName || categorySlug,
    ...colors[colorIndex]
  };
};

// Helper function to get background color class based on variant
const getBackgroundColorClass = (variant: string) => {
  switch (variant) {
    case 'surface':
      return 'bg-surface';
    case 'surfaceLight':
      return 'bg-surface-light';
    case 'beige':
      return 'bg-beige';
    case 'beigeLight':
      return 'bg-beige-light';
    default:
      return 'bg-white';
  }
};

export function TemplateCardComponent({ template, backgroundColorVariant = 'default' }: TemplateCardProps) {
  const categoryConfig = getCategoryConfig(template.template_categories?.slug, template.template_categories?.name);
  const IconComponent = categoryConfig.icon;
  const backgroundColorClass = getBackgroundColorClass(backgroundColorVariant);

  // Truncate summary to ensure consistent card heights (allow for 4 lines)
  const truncatedDescription = template.summary && template.summary.length > 240
    ? template.summary.substring(0, 240) + '...'
    : template.summary || 'No description available.';



  return (
    <Link href={`/templates/${template.slug}`} className="block group">
      <div className={cn(
        backgroundColorClass,
        "rounded-lg shadow-md overflow-hidden",
        "transition-all duration-200 hover:shadow-xl",
        "h-[280px] flex flex-col relative" // Fixed height for consistency + relative for absolute positioning
      )}>
        {/* Top color accent bar - dynamic based on category */}
        <div className={cn("h-1.5", categoryConfig.accentBgClass)} />


        {/* Card content with flex layout */}
        <div className="p-4 flex-1 flex flex-col">
          {/* Header with title */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors leading-tight">
                {template.title}
              </h3>
            </div>
          </div>

          {/* Description */}
          <div className="flex-1 mb-4">
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">
              {truncatedDescription}
            </p>
          </div>

          {/* Bottom section with category pill (left) and pricing (right) */}
          <div className="mt-auto flex items-center justify-between">
            <Badge 
              className={cn(
                "text-xs font-medium text-white",
                categoryConfig.accentBgClass
              )}
            >
              {template.template_categories?.name || 'Template'}
            </Badge>
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-sm font-medium",
                template.is_freebie ? "text-green" : "text-yellow"
              )}>
                {template.is_freebie ? 'Free' : 'Premium'}
              </span>
              {template.published_at && (
                <span className="text-sm text-gray-500">
                  {new Date(template.published_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

